import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { logUsageSession } from '../services/usageService';

const MIN_SESSION_SECONDS = 15;
const HEARTBEAT_INTERVAL_MS = 60 * 1000;

interface UseUsageTrackerOptions {
  enabled?: boolean;
}

export const useUsageTracker = ({ enabled = true }: UseUsageTrackerOptions = {}) => {
  const location = useLocation();
  const sessionStartRef = useRef<number | null>(null);
  const isLoggingUsageRef = useRef(false);
  const isEnabledRef = useRef(enabled);
  const routeKey = `${location.pathname}${location.search}${location.hash}`;
  const previousRouteRef = useRef(routeKey);

  const resetSessionStart = useCallback(() => {
    sessionStartRef.current = Date.now();
  }, []);

  const flushUsage = useCallback(async () => {
    if (!isEnabledRef.current || isLoggingUsageRef.current || sessionStartRef.current === null) {
      return;
    }

    const startAt = sessionStartRef.current;
    const now = Date.now();
    const durationSeconds = Math.round((now - startAt) / 1000);

    if (durationSeconds < MIN_SESSION_SECONDS) {
      return;
    }

    isLoggingUsageRef.current = true;
    try {
      await logUsageSession({
        sessionStart: new Date(startAt).toISOString(),
        sessionEnd: new Date(now).toISOString(),
        durationSeconds,
      });
      sessionStartRef.current = now;
    } catch (error) {
      console.error('Failed to log usage session:', error);
    } finally {
      isLoggingUsageRef.current = false;
    }
  }, []);

  useEffect(() => {
    isEnabledRef.current = enabled;
    if (!enabled) {
      sessionStartRef.current = null;
      return;
    }
    if (sessionStartRef.current === null) {
      resetSessionStart();
    }
  }, [enabled, resetSessionStart]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    resetSessionStart();

    const heartbeatId = window.setInterval(() => {
      void flushUsage();
    }, HEARTBEAT_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        void flushUsage();
        return;
      }
      resetSessionStart();
    };

    const handlePageHide = () => {
      void flushUsage();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.clearInterval(heartbeatId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      void flushUsage();
    };
  }, [enabled, flushUsage, resetSessionStart]);

  useEffect(() => {
    if (!enabled) {
      previousRouteRef.current = routeKey;
      return;
    }

    if (previousRouteRef.current === routeKey) {
      return;
    }

    previousRouteRef.current = routeKey;
    const flushPromise = flushUsage();
    resetSessionStart();
    void flushPromise;
  }, [enabled, flushUsage, resetSessionStart, routeKey]);
};
